import { groupFolderRows } from 'flyt/lib/folderTree/groupFolderRows'
import { PagedRows } from 'flyt/ui/PagedRows/PagedRows'
import { ColumnResizeHandle } from './ColumnResizeHandle'
import { useColumnWidths } from './useColumnWidths'
import type { FolderNode } from 'flyt/lib/folderTree/folderTree'
export { DataTableColumnsMenu } from './DataTableColumnsMenu'
import { Fragment, useMemo, useState } from 'react'
import type {
  MouseEventHandler,
  ReactElement,
  RefCallback,
} from 'react'
import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import type { ColumnDef, Row as TanstackRow, SortingState } from '@tanstack/react-table'
import { useLocale, useT } from 'flyt/lib/language/language'
import { ArrowDownIcon, ArrowUpIcon, ChevronDownIcon } from 'flyt/ui/Icons/Icons'
import { StatesEmptyState } from 'flyt/ui/StatesEmptyState/StatesEmptyState'
import { Table, TBody, Td, Th, THead, Tr } from 'flyt/ui/Table/Table'
import type {
  DataTableColumn,
  DataTableColumnView,
  DataTableColumnWidth,
  DataTableProps,
  DataTableSort,
} from './DataTable.types'
import { ColumnDragHandle } from './ColumnDragHandle'
import { useColumnReorder } from './useColumnReorder'
import { moveColumn, orderIds } from './tableOrdering'

/**
 * data-table — the generic, feature-blind sortable table composite.
 *
 * ⛔ The single confined home of @tanstack/react-table: no TanStack type
 * crosses the contract. Rows and column descriptors arrive as props; the unit
 * fetches nothing, filters nothing, and holds view state. Column widths can be stored locally.
 *
 * Sorting is single-column, cycling ascending → descending → unsorted, and
 * collates per locale against the text the user SEES (the display label when
 * the column defines one). Label and locale are read fresh at comparison time,
 * so a locale switch re-sorts without a remount.
 *
 * The column view (hidden ids + order) is controlled through the optional
 * `view`/`onViewChange` pair, with internal state as the fallback — either way
 * ephemeral, never persisted. Dragging a header grip onto another header reorders; a plain
 * click still sorts. `DataTableColumnsMenu` is the separately mountable
 * visibility menu, so a host can place its trigger in its own action row.
 */

/**
 * THE COLUMNS ARE FIXED, AND THE READER MOVES THEM. Every column starts at the
 * share its declared intent is worth and keeps it whatever lands in it — a grid
 * measured from the content re-measures on every change and slides the table
 * sideways under the reader's eye. A width the reader disagrees with is
 * answered by dragging the header edge.
 *
 * A SHARE, NOT A PIXEL COUNT. Fixed pixel widths make the table's total a
 * number nobody checks against the screen it lands on. Shares always add up to
 * the table, on any screen, at any zoom.
 */
const COLUMN_SHARES: Record<DataTableColumnWidth, number> = {
  narrow: 0.7,
  default: 1,
  wide: 1.8,
}

/** The text a column shows (and therefore sorts by) for a row, read at call time. */
function displayText<Row>(column: DataTableColumn<Row>, row: Row): string {
  const raw = column.value(row)
  return column.label ? column.label(raw) : raw
}

/** The default view: nothing hidden, pure descriptor order. */
const DEFAULT_VIEW: DataTableColumnView = { hiddenIds: [], order: [] }

export function DataTable<Row>({
  rows,
  columns,
  rowId,
  emptyMessage,
  initialSort,
  onRowActivate,
  view,
  onViewChange,
  sort,
  onSortChange,
  grouping,
  pageSize,
  pageResetKey = '',
  widthStorageKey,
}: DataTableProps<Row>) {
  const t = useT('common')
  const locale = useLocale()
  const collator = useMemo(() => new Intl.Collator(locale), [locale])

  // Seeded once, on mount (default state only): later re-renders with the
  // same prop never overwrite user interaction.
  const [internalSorting, setInternalSorting] = useState<SortingState>(() =>
    initialSort ? [{ id: initialSort.columnId, desc: initialSort.direction === 'desc' }] : [],
  )
  // The sort: the controlled prop when supplied, else the unit's own state.
  // Same shape of bargain as the column view above — a host takes it over only
  // when the host also SHOWS it.
  // ⚠️ MEMOISED ON PURPOSE — see the note on `state` below. A fresh array here
  // on every render is an infinite render loop.
  const sorting = useMemo<SortingState>(
    () =>
      sort === undefined
        ? internalSorting
        : sort === 'served'
          ? []
          : [{ id: sort.columnId, desc: sort.direction === 'desc' }],
    [sort, internalSorting],
  )
  const setSorting = (next: SortingState | ((prev: SortingState) => SortingState)): void => {
    const resolved = typeof next === 'function' ? next(sorting) : next
    if (sort === undefined) {
      setInternalSorting(resolved)
    }
    const first = resolved[0]
    const reported: DataTableSort =
      first === undefined ? 'served' : { columnId: first.id, direction: first.desc ? 'desc' : 'asc' }
    onSortChange?.(reported)
  }

  // The column view: the controlled prop when supplied, else the unit's own
  // ephemeral state (never persisted, never part of any query/selection).
  const [internalView, setInternalView] = useState<DataTableColumnView>(DEFAULT_VIEW)
  const columnView = view ?? internalView
  const applyView = (next: DataTableColumnView): void => {
    if (view === undefined) {
      setInternalView(next)
    }
    onViewChange?.(next)
  }

  // Positive weights keep all visible columns normalized to the table width.
  const [columnWidths, setColumnWidths] = useColumnWidths(widthStorageKey)
  const weightOf = (column: DataTableColumn<Row>) => columnWidths[column.id] ?? COLUMN_SHARES[column.width ?? 'default']

  // The expanded group ids — ephemeral view state. The grouping config's
  // default stays live until the user's first toggle (groups often arrive
  // async, after mount — a mount-time seed would miss them); from that first
  // toggle on, the user's own set wins and the default is never re-applied.
  const [expandedOverride, setExpandedOverride] = useState<ReadonlySet<string> | null>(null)
  const expandedGroupIds: ReadonlySet<string> =
    grouping?.expandedIds !== undefined ? new Set(grouping.expandedIds) : expandedOverride ?? new Set(grouping?.initialExpandedIds ?? [])
  const toggleGroup = (groupId: string): void => {
    const next = new Set(expandedGroupIds)
    if (next.has(groupId)) {
      next.delete(groupId)
    } else {
      next.add(groupId)
    }
    if (grouping?.expandedIds === undefined) setExpandedOverride(next)
    grouping?.onExpandedIdsChange?.([...next])
  }

  // The current descriptors in view order, and the visible subset of them.
  const orderedColumns = useMemo<readonly DataTableColumn<Row>[]>(() => {
    const byId = new Map(columns.map((column) => [column.id, column]))
    return orderIds(
      columnView.order,
      columns.map((column) => column.id),
    ).flatMap((id) => byId.get(id) ?? [])
  }, [columns, columnView.order])

  const visibleColumns = useMemo(
    () => orderedColumns.filter((column) => !columnView.hiddenIds.includes(column.id)),
    [orderedColumns, columnView.hiddenIds],
  )

  const reorder = (sourceId: string, targetId: string): void => {
    const ids = orderedColumns.map((column) => column.id)
    const order = moveColumn(ids, sourceId, targetId)
    if (order !== ids) applyView({ ...columnView, order })
  }
  const drag = useColumnReorder(reorder)

  const columnDefs = useMemo<ColumnDef<Row, string>[]>(
    () =>
      columns.map((column) => ({
        id: column.id,
        accessorFn: (row) => column.value(row),
        enableSorting: column.sortable ?? false,
        sortingFn: (rowA, rowB) =>
          collator.compare(displayText(column, rowA.original), displayText(column, rowB.original)),
      })),
    [columns, collator],
  )

  // What the visible columns' shares add up to — the denominator that turns a
  // share into a percentage. Hiding a column hands its share to the rest.
  const totalShare = visibleColumns.reduce(
    (total, column) => total + weightOf(column),
    0,
  )

  const tableState = useMemo(() => ({ sorting }), [sorting])

  const table = useReactTable({
    data: rows as Row[],
    columns: columnDefs,
    // ⚠️ THE STATE OBJECT IS COMPARED BY IDENTITY, NOT BY VALUE. The table
    // rebuilds its sorted row model whenever this array is a different object,
    // and rebuilding writes table state back — so an array rebuilt on every
    // render re-renders this component forever and locks the tab up. Both this
    // object and `sorting` inside it must survive a render unchanged.
    state: tableState,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => rowId(row),
    enableSortingRemoval: true,
    enableMultiSort: false,
    sortDescFirst: false,
  })

  return (
    <Table>
      {/* The grid itself: each column's share of the table, as a percentage.
          A width is a runtime number the reader can drag, so it is the one
          visual value that cannot be a class — it is a measurement, not a theme
          decision. */}
      <colgroup>
        {visibleColumns.map((column) => (
          <col
            key={column.id}
            style={{
              width: `${(weightOf(column) / totalShare) * 100}%`,
            }}
          />
        ))}
      </colgroup>
      <THead>
        <Tr>
          {visibleColumns.map((column, index) => {
            const tableColumn = table.getColumn(column.id)
            const direction = tableColumn?.getIsSorted() ?? false
            const dragHandle = <ColumnDragHandle label={column.header}
              active={drag.stateOf(column.id) === 'source'} axis="horizontal"
              {...drag.handleProps(column.id)} onMove={(offset) => {
                const target = visibleColumns[index + offset]
                if (target) reorder(column.id, target.id)
              }} />
            const nextColumn = visibleColumns[index + 1]
            const resizeHandle = nextColumn ? <ColumnResizeHandle
              label={column.header}
              percent={weightOf(column) / totalShare * 100}
              onResize={(fraction) => {
                const pairWeight = weightOf(column) + weightOf(nextColumn)
                setColumnWidths({ ...columnWidths, [column.id]: pairWeight * fraction, [nextColumn.id]: pairWeight * (1 - fraction) })
              }}
            /> : null
            if (column.sortable !== true) {
              // Inert header: plain text, no control, no aria-sort.
              return (
                <Th key={column.id} scope="col" aria-label={column.header} data-column-id={column.id} dragState={drag.stateOf(column.id)} {...drag.targetProps(column.id)}>
                  <div className="flex min-w-0 items-center gap-1">{dragHandle}<span className="truncate">{column.header}</span></div>
                  {resizeHandle}
                </Th>
              )
            }
            return (
              <Th
                key={column.id}
                scope="col"
                aria-label={column.header}
                data-column-id={column.id}
                dragState={drag.stateOf(column.id)}
                {...drag.targetProps(column.id)}
                aria-sort={
                  direction === 'asc'
                    ? 'ascending'
                    : direction === 'desc'
                      ? 'descending'
                      : undefined
                }
              >
                {/* A SORTABLE HEADER LIGHTS UP UNDER THE POINTER. The arrow
                    only appears once the column IS sorted, so before the first
                    click nothing told the reader the header was a control at
                    all; the hover ink is that answer, and it is the same ink
                    every hovered control in the portal uses. */}
                <div className="flex min-w-0 items-center gap-1">
                  {dragHandle}
                  <button
                    type="button"
                    className="inline-flex max-w-full cursor-pointer items-center gap-1 truncate transition-colors hover:text-text"
                    onClick={() => tableColumn?.toggleSorting()}
                  >
                    {column.header}
                    {direction === 'asc' ? <ArrowUpIcon width={14} height={14} /> : null}
                    {direction === 'desc' ? <ArrowDownIcon width={14} height={14} /> : null}
                  </button>
                </div>
                {resizeHandle}
              </Th>
            )
          })}
        </Tr>
      </THead>
      <TBody>
        {rows.length === 0 && !(grouping?.showEmptyGroups && grouping.groups.length > 0) ? (
          <Tr>
            <Td colSpan={visibleColumns.length}>
              <StatesEmptyState description={emptyMessage} />
            </Td>
          </Tr>
        ) : (
          (() => {
            const renderRow = (tableRow: TanstackRow<Row>, depth = 0, rowRef?: RefCallback<HTMLElement>): ReactElement => {
              // Opt-in row activation: clicks landing on an interactive cell
              // control (the title button, a row-action button/link, a form
              // field) belong to that control; the row activates only from its
              // plain areas. Absent onRowActivate → the prop is not passed at
              // all (exact-optional), leaving a plain non-interactive row.
              const onRowClick: MouseEventHandler<HTMLTableRowElement> | undefined = onRowActivate
                ? (event) => {
                    if ((event.target as HTMLElement).closest('button, a, input, select')) {
                      return
                    }
                    onRowActivate(tableRow.original)
                  }
                : undefined
              return (
                <Tr key={tableRow.id} {...(rowRef ? { rowRef } : {})} {...(onRowClick ? { onClick: onRowClick } : {})}>
                  {visibleColumns.map((column, index) => (
                    // A row inside an open group is stepped in by its FIRST
                    // cell only. The tab says "this belongs to the shelf above";
                    // indenting the whole row would move the columns, which is
                    // the thing the fixed grid exists to prevent.
                    <Td key={column.id} indent={index === 0 ? depth : 0}>
                      {column.cell
                        ? column.cell(tableRow.original)
                        : displayText(column, tableRow.original)}
                    </Td>
                  ))}
                </Tr>
              )
            }
            const sortedRows = table.getRowModel().rows
            const resetKey = JSON.stringify([pageResetKey, sorting])
            const renderPage = (pageRows: readonly TanstackRow<Row>[], id: string, label: string, depth = 0) => (
              <PagedRows key={id} rows={pageRows} resetKey={resetKey} label={label}
                {...(pageSize !== undefined ? { pageSize } : {})}>
                {(visible, controls, pageStartRef) => <>
                  {visible.map((row, index) => renderRow(row, depth, index === 0 ? pageStartRef : undefined))}
                  {controls ? <Tr><Td colSpan={visibleColumns.length} indent={depth}>{controls}</Td></Tr> : null}
                </>}
              </PagedRows>
            )
            if (grouping === undefined) {
              return renderPage(sortedRows, 'flat', t('paging.results'))
            }
            const tree = groupFolderRows(grouping.groups, sortedRows, (row) => grouping.groupIdOf(row.original))
            const { rowsByFolder, counts, unmatched } = tree
            const renderGroup = (group: FolderNode, depth: number): ReactElement | null => {
              const count = counts.get(group.value) ?? 0
              if (!grouping.showEmptyGroups && count === 0) return null
              const expanded = expandedGroupIds.has(group.value)
              return <Fragment key={group.value}>
                <Tr groupHeader nestedGroup={depth > 0} onClick={(event) => {
                  if (!(event.target as HTMLElement).closest('button, a, input, select')) toggleGroup(group.value)
                }}>
                  <Td colSpan={visibleColumns.length} indent={depth}>
                    <button type="button" aria-expanded={expanded} onClick={() => toggleGroup(group.value)}
                      className="flex w-full cursor-pointer items-center gap-2 text-left text-base font-semibold text-text">
                      <ChevronDownIcon width={16} height={16} className={`shrink-0 text-accent transition-transform ${expanded ? '' : '-rotate-90'}`} />
                      <span className="truncate">{group.label}</span>
                      <span className="rounded-full border border-border px-2 py-0.5 text-xs font-medium text-text-muted">{count}</span>
                    </button>
                  </Td>
                </Tr>
                {expanded ? <>
                  {group.children.map((child) => renderGroup(child, depth + 1))}
                  {renderPage(rowsByFolder.get(group.value) ?? [], group.value, group.path.map((part) => part.label).join(' / '), depth + 1)}
                </> : null}
              </Fragment>
            }
            // Unresolved rows remain readable without inventing a folder.
            return <>{tree.roots.map((group) => renderGroup(group, 0))}{renderPage(unmatched, 'unmatched', t('paging.results'))}</>

          })()
        )}
      </TBody>
    </Table>
  )
}
